import React, { useState } from "react";
import { toast } from "sonner";
import { X, Trash, FloppyDisk } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { advisorHeaders, CATEGORY_ORDER, scoreTone } from "../../lib/clarity";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const CAT = { cash: "Cash Flow", tax: "Tax", protection: "Protection", networth: "Net Worth", retirement: "Retirement", context: "Context", goals: "Goals" };
const sel = "input-exodus !py-1.5 !text-xs";

function AnswerEditor({ q, value, onChange }) {
  if (q.type === "bucket_mix") {
    const v = value || {};
    return (
      <div className="space-y-1">
        {q.buckets.map((b) => (
          <select key={b.key} data-testid={`admin-answer-edit-${q.id}-${b.key}`} className={sel} value={v[b.key] || ""} onChange={(e) => onChange({ ...v, [b.key]: e.target.value || null })}>
            <option value="">{b.label}: not answered</option>
            {q.options.map((o) => <option key={o.value} value={o.value}>{b.label}: {o.label}</option>)}
          </select>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const arr = value || [];
    return (
      <div className="flex flex-wrap gap-1">
        {q.options.map((o) => (
          <button key={o.value} type="button" data-testid={`admin-answer-edit-${q.id}-${o.value}`} onClick={() => onChange(arr.includes(o.value) ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
            className={`text-[10px] px-2 py-1 border ${arr.includes(o.value) ? "border-exodus-gold text-exodus-gold" : "border-exodus-border text-exodus-muted"}`}>{o.label}</button>
        ))}
      </div>
    );
  }
  return (
    <select data-testid={`admin-answer-edit-${q.id}`} className={sel} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">— not answered —</option>
      {q.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function AnswersTable({ d, edits, setEdits }) {
  const [showAll, setShowAll] = useState(false);
  const qs = d.questions.filter((q) => showAll || q.id in d.answers || q.id in edits || q.tier === "quick");
  return (
    <>
      <button data-testid="admin-answers-toggle-all" onClick={() => setShowAll((s) => !s)} className="text-xs gold-text underline mb-3">
        {showAll ? "Hide unanswered deeper questions" : "Show all questions (incl. unanswered deeper tier)"}
      </button>
      <table className="w-full text-xs">
        <tbody>
          {qs.map((q) => {
            const value = q.id in edits ? edits[q.id] : d.answers[q.id];
            const answered = d.answers[q.id] != null;
            return (
              <tr key={q.id} data-testid={`admin-answer-${q.id}`} className={`border-t border-exodus-border align-top ${answered ? "" : "opacity-70"}`}>
                <td className="py-2 pr-3 text-exodus-muted w-24">{CAT[q.category]}{q.tier === "deep" && <span className="block text-[9px] eyebrow">deep</span>}</td>
                <td className="py-2 pr-3 text-exodus-text/90">{q.prompt}{!answered && <span className="block text-[10px] text-orange-300">not answered</span>}</td>
                <td className="py-2 w-60"><AnswerEditor q={q} value={value} onChange={(v) => setEdits({ ...edits, [q.id]: v })} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

export default function AdminClarityDetail({ d, leadCategories, onClose, onChanged }) {
  const [edits, setEdits] = useState({});
  const [notes, setNotes] = useState(d.notes || "");
  const [cat, setCat] = useState(d.lead_category);
  const [saving, setSaving] = useState(false);
  const c = d.contact || {};
  const r = d.result;

  const save = async () => {
    setSaving(true);
    try {
      const body = { notes, ...(Object.keys(edits).length ? { answers: edits } : {}), ...(cat !== d.lead_category ? { lead_category: cat } : {}) };
      const res = await api.patch(`/admin/clarity/${d.id}`, body, advisorHeaders());
      toast.success("Saved. Score recalculated where answers changed.");
      setEdits({}); onChanged(res.data);
    } catch { toast.error("Could not save changes."); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Permanently delete this assessment (data deletion request)?")) return;
    await api.delete(`/admin/clarity/${d.id}`, advisorHeaders());
    toast.success("Assessment deleted."); onChanged(null);
  };

  return (
    <div data-testid="admin-clarity-detail" className="fixed inset-0 z-[70] flex justify-end">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <aside className="relative w-full max-w-3xl h-full overflow-y-auto bg-exodus-bg border-l border-exodus-gold/40 p-6 sm:p-10">
        <button data-testid="admin-clarity-detail-close" onClick={onClose} className="absolute top-5 right-5 w-9 h-9 border border-exodus-border flex items-center justify-center hover:border-exodus-gold"><X size={16} /></button>
        <p className="eyebrow">Clarity assessment · {d.tier}</p>
        <h2 className="font-display text-3xl mt-2">{c.first_name || "Anonymous visitor"} <span className={`ml-2 ${scoreTone(r.overall)}`}>{r.overall ?? "—"}</span><span className="text-exodus-muted text-lg">/100</span></h2>
        <p className="text-sm text-exodus-muted">{r.band} · confidence {r.confidence_label}{r.provisional ? " · provisional" : ""} · {fmt(d.created_at)}</p>

        <div className="grid sm:grid-cols-2 gap-4 mt-6 text-sm">
          <div className="card-surface p-4 space-y-1">
            <p className="eyebrow text-[10px] mb-2">Contact</p>
            <p>{c.email || "—"}</p><p>{c.phone || "No phone"} · {c.state || "—"}</p>
            <p className="text-xs text-exodus-muted">Prefers {c.preferred_contact || "—"} · timeframe {c.timeframe || "—"}</p>
            <p className="text-xs text-exodus-muted">Consult requested: {String(!!c.consult_requested)} · Contact consent: {String(!!c.consent_contact)} · Email consent: {String(!!c.consent_email)}</p>
            <p className="text-xs text-exodus-muted">IP {d.ip_address || "—"} · {d.booking_clicked_at ? "Clicked Book" : "No booking click"} · {d.callback_requested_at ? "Callback requested" : ""}</p>
          </div>
          <div className="card-surface p-4">
            <p className="eyebrow text-[10px] mb-2">Pathway</p>
            <p>{d.pathway?.label}</p>
            <p className="text-xs text-exodus-muted mt-1">Top priority: {d.pathway?.top_goal_label || "—"} · service fit: {d.pathway?.service_fit}</p>
            <div className="grid grid-cols-5 gap-2 mt-4">
              {CATEGORY_ORDER.map((k) => (
                <div key={k} className="text-center"><p className={`font-display text-2xl ${scoreTone(r.categories[k].score)}`}>{r.categories[k].score ?? "—"}</p><p className="text-[9px] text-exodus-muted">{k}</p></div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <p className="eyebrow mb-3">All answers ({d.labelled_answers.length} answered) — correct or fill in an input, then Save to re-score</p>
          <AnswersTable d={d} edits={edits} setEdits={setEdits} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          <label className="block"><span className="eyebrow text-[10px]">Lead category</span>
            <select data-testid="admin-clarity-lead-cat" className="input-exodus mt-2" value={cat} onChange={(e) => setCat(e.target.value)}>
              {leadCategories.map((x) => <option key={x}>{x}</option>)}
            </select>
          </label>
          <label className="block sm:col-span-2"><span className="eyebrow text-[10px]">Reviewer notes</span>
            <textarea data-testid="admin-clarity-notes" rows={3} className="input-exodus mt-2" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <button data-testid="admin-clarity-save" onClick={save} disabled={saving} className="btn-gold"><FloppyDisk size={16} /> {saving ? "Saving…" : "Save review"}</button>
          <button data-testid="admin-clarity-delete" onClick={remove} className="btn-ghost !border-red-900 !text-red-400"><Trash size={16} /> Delete record</button>
        </div>

        {(d.consent_log?.length > 0 || d.review_log?.length > 0) && (
          <div className="mt-10 text-xs text-exodus-muted space-y-2">
            <p className="eyebrow text-[10px]">Audit trail</p>
            {d.consent_log?.map((l, i) => <p key={`c${i}`}>{fmt(l.at)} — consent: contact {String(l.consent_contact)}{l.consent_email != null ? `, email ${l.consent_email}` : ""} · {l.text?.slice(0, 60)}</p>)}
            {d.review_log?.map((l, i) => <p key={`r${i}`}>{fmt(l.at)} — {l.by}: {l.changes.map((x) => `${x.field} ${x.from}→${x.to}`).join(", ") || "notes updated"}</p>)}
          </div>
        )}
      </aside>
    </div>
  );
}
